import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Logo } from '../Logo';

/* ═══════════════════════════════════════════════════════════════
   FOOTER — Editorial edition
   ───────────────────────────────────────────────────────────────
   Mirrors the Solutions/Models pages:
     Fraunces masthead · Nasalization labels · JetBrains Mono tags
   Four zones: masthead + newsletter, link grid, contact, copyright.
   ═══════════════════════════════════════════════════════════════ */

const SP = {
  bg:          'transparent',
  canvas:      'rgba(0,0,0,0.4)',
  canvasCool:  'rgba(0,0,0,0.6)',
  ink:         '#FFFFFF',
  inkSoft:     'rgba(255,255,255,0.9)',
  muted:       'rgba(255,255,255,0.7)',
  subtle:      'rgba(255,255,255,0.4)',
  faint:       'rgba(255,255,255,0.2)',
  line:        'rgba(255,255,255,0.1)',
  accentA:     '#7C3AED',
  accentB:     '#A78BFA',
  accentGrad:  'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)',
  accentTint:  'rgba(124,58,237,0.1)',
  accentTintB: 'rgba(124,58,237,0.2)',
  accentDeep:  '#C4B5FD',
};
const SERIF = '"Nasalization", "Instrument Serif", Georgia, serif';
const MONO  = '"JetBrains Mono", ui-monospace, Menlo, monospace';
const TECH  = '"Nasalization", "Orbitron", "Space Grotesk", ui-sans-serif, system-ui, sans-serif';

function useBreakpoint() {
  const read = () => {
    if (typeof window === 'undefined') return { isMobile: false, isTablet: false, isDesktop: true };
    const w = window.innerWidth;
    return { isMobile: w < 640, isTablet: w >= 640 && w < 1024, isDesktop: w >= 1024 };
  };
  const [bp, setBp] = useState(read);
  useEffect(() => {
    const onResize = () => setBp(read());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return bp;
}

/* ─── Link structure ──────────────────────────────────────────── */
const LINK_COLUMNS = [
  {
    label: 'Platform',
    links: [
      { name: 'Solutions',  url: '/solutions' },
      { name: 'Models',     url: '/models'    },
      { name: 'Workspace',  url: '/workspace' },
      { name: 'Pricing',    url: '#pricing', soon: true },
      { name: 'Enterprise', url: '#enterprise', soon: true },
    ],
  },
  {
    label: 'Company',
    links: [
      { name: 'About',     url: '#about', soon: true     },
      { name: 'Changelog', url: '#changelog', soon: true },
      { name: 'Careers',   url: '#careers', soon: true   },
      { name: 'Blog',      url: '#blog', soon: true      },
    ],
  },
  {
    label: 'Legal',
    links: [
      { name: 'Terms of Service', url: '/terms-of-service' },
      { name: 'Privacy Policy',   url: '/privacy-policy'   },
      { name: 'Cookie Policy',    url: '/cookie-policy'    },
    ],
  },
  {
    label: 'Connect',
    links: [
      { name: 'GitHub', url: 'https://github.com/dagchain-official/daggpt-landing', external: true },
      { name: 'Email',  url: 'mailto:support@daggpt.network' },
    ],
  },
];

export function Footer() {
  const { isMobile, isTablet } = useBreakpoint();
  const year = new Date().getFullYear();

  return (
    <footer style={{
      position: 'relative',
      zIndex: 2,
      marginTop: isMobile ? '40px' : '80px',
      background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(10,10,20,0.8) 100%)',
      borderTop: `1px solid ${SP.line}`,
      overflow: 'hidden',
    }}>
      {/* Ambient violet orb hugging the top edge */}
      <div aria-hidden style={{
        position: 'absolute',
        top: '-180px', left: '50%',
        transform: 'translateX(-50%)',
        width: '900px', height: '400px',
        background: 'radial-gradient(ellipse, rgba(167,139,250,0.22) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />

      <div style={{
        position: 'relative',
        maxWidth: '1340px',
        margin: '0 auto',
        padding: isMobile ? '60px 20px 32px' : isTablet ? '72px 32px 40px' : '88px 40px 44px',
      }}>
        {/* Logo sits at the very top, left-aligned. */}
        <div style={{
          marginBottom: isMobile ? '40px' : '56px',
          display: 'inline-block',
          transform: 'scale(1.1)',
          transformOrigin: 'left center',
        }}>
          <Logo />
        </div>

        <LinkGrid isMobile={isMobile} isTablet={isTablet} />

        <div style={{
          height: '1px',
          background: SP.line,
          margin: isMobile ? '40px 0 24px' : '56px 0 28px',
        }} />

        <BottomRow year={year} isMobile={isMobile} isTablet={isTablet} />
      </div>
    </footer>
  );
}

/* ─── Masthead (UNUSED NOW, kept for reference) ──────────────── */
// eslint-disable-next-line no-unused-vars
function MastheadRow({ isMobile, isTablet }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile || isTablet ? '1fr' : '1.3fr 1fr',
      gap: isMobile ? '48px' : isTablet ? '56px' : '80px',
      alignItems: 'flex-end',
    }}>
      {/* LEFT — giant serif tagline + subline */}
      <div>
        <h2 style={{
          fontFamily: SERIF,
          fontSize: 'clamp(40px, 5.4vw, 76px)',
          lineHeight: 1.02,
          letterSpacing: '-0.035em',
          color: SP.ink,
          fontWeight: 400,
          margin: '0 0 22px',
          paddingBottom: '0.1em',
          maxWidth: '640px',
        }}>
          The{' '}
          <em style={{
            fontStyle: 'italic',
            background: SP.accentGrad,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            padding: '0 0.06em',
          }}>
            right model
          </em>
          , for the{' '}
          <em style={{
            fontStyle: 'italic',
            background: SP.accentGrad,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            padding: '0 0.06em',
          }}>
            right job
          </em>
          .
        </h2>

        <p style={{
          fontFamily: SERIF,
          fontStyle: 'italic',
          fontSize: isMobile ? '16px' : '18px',
          lineHeight: 1.55,
          color: SP.muted,
          margin: 0,
          maxWidth: '520px',
        }}>
          One login. Every frontier model. Every workflow. One unified bill.
        </p>
      </div>

      {/* RIGHT — newsletter card */}
      <NewsletterPanel isMobile={isMobile} />
    </div>
  );
}

/* ─── Newsletter: premium violet-tinted form ──────────────────── */
function NewsletterPanel({ isMobile }) {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [focus, setFocus] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      setSubscribed(true);
      setTimeout(() => { setSubscribed(false); setEmail(''); }, 3200);
    }
  };

  return (
    <div style={{
      position: 'relative',
      padding: isMobile ? '28px 22px' : '36px 32px',
      borderRadius: '22px',
      background: 'rgba(255, 255, 255, 0.02)',
      backdropFilter: 'blur(8px)',
      overflow: 'hidden',
      boxShadow: '0 30px 80px -40px rgba(124,58,237,0.30)',
    }}>
      {/* Corner orb */}
      <div style={{
        position: 'absolute', top: '-40%', right: '-20%',
        width: '280px', height: '280px',
        background: 'radial-gradient(circle, rgba(167,139,250,0.35) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '14px',
        }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: SP.accentGrad }} />
          <span style={{
            fontFamily: MONO, fontSize: '10px', letterSpacing: '0.26em',
            color: SP.accentDeep, fontWeight: 600, textTransform: 'uppercase',
          }}>
            Dispatch — Monthly
          </span>
        </div>

        <h3 style={{
          fontFamily: SERIF,
          fontSize: 'clamp(22px, 2.4vw, 30px)',
          lineHeight: 1.1,
          letterSpacing: '-0.025em',
          color: SP.ink,
          fontWeight: 400,
          margin: '0 0 10px',
          paddingBottom: '0.08em',
        }}>
          Notes from the{' '}
          <em style={{
            fontStyle: 'italic',
            background: SP.accentGrad,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            padding: '0 0.06em',
          }}>
            frontier
          </em>
          .
        </h3>

        <p style={{
          fontSize: '14px',
          lineHeight: 1.6,
          color: SP.muted,
          margin: '0 0 22px',
        }}>
          New model drops, workflow essays, and the occasional founder letter.
          One email a month. No fluff.
        </p>

        <form onSubmit={onSubmit} style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          borderRadius: '999px',
          background: '#FFFFFF',
          border: `1px solid ${focus ? SP.accentA : SP.line}`,
          boxShadow: focus
            ? '0 0 0 4px rgba(167,139,250,0.16)'
            : '0 1px 2px rgba(10,10,10,0.03)',
          padding: '4px 4px 4px 18px',
          transition: 'border 0.2s, box-shadow 0.2s',
        }}>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            placeholder="you@domain.com"
            disabled={subscribed}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontFamily: 'inherit',
              fontSize: '14px',
              color: SP.ink,
              letterSpacing: '-0.01em',
              padding: '10px 0',
              minWidth: 0,
            }}
          />
          <motion.button
            type="submit"
            whileTap={{ scale: 0.96 }}
            disabled={subscribed}
            style={{
              border: 'none',
              background: subscribed ? '#10B981' : SP.ink,
              color: '#FFFFFF',
              padding: '10px 20px',
              borderRadius: '999px',
              fontFamily: MONO,
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              cursor: subscribed ? 'default' : 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background 0.25s',
              boxShadow: '0 10px 22px -10px rgba(124,58,237,0.5)',
            }}
          >
            {subscribed ? 'Subscribed ✓' : 'Subscribe →'}
          </motion.button>
        </form>

        <p style={{
          fontFamily: MONO,
          fontSize: '9.5px',
          letterSpacing: '0.18em',
          color: SP.subtle,
          fontWeight: 600,
          textTransform: 'uppercase',
          margin: '16px 0 0',
        }}>
          Unsubscribe with one click. We will never sell your email.
        </p>
      </div>
    </div>
  );
}

/* ─── Link grid: 4 cols desktop, 2 cols tablet, 1 col mobile ──── */
function LinkGrid({ isMobile, isTablet }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : isTablet ? 'repeat(4, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? '36px 24px' : '32px',
    }}>
      {LINK_COLUMNS.map((col) => (
        <LinkColumn key={col.label} column={col} />
      ))}
    </div>
  );
}

function LinkColumn({ column }) {
  return (
    <div>
      <div style={{
        fontFamily: TECH,
        fontSize: '11px',
        letterSpacing: '0.22em',
        color: SP.ink,
        fontWeight: 600,
        textTransform: 'uppercase',
        marginBottom: '18px',
        paddingBottom: '10px',
        borderBottom: `1px solid ${SP.accentTintB}`,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: SP.accentGrad }} />
        {column.label}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {column.links.map((link) => (
          <FooterLink key={link.name} link={link} />
        ))}
      </ul>
    </div>
  );
}

function FooterLink({ link }) {
  const [hover, setHover] = useState(false);
  const { name, url, soon, external } = link;

  const content = (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      fontFamily: SERIF,
      fontSize: '16px',
      color: hover ? SP.ink : SP.inkSoft,
      letterSpacing: '-0.015em',
      fontWeight: 400,
      transition: 'color 0.2s',
    }}>
      <motion.span
        animate={{ width: hover ? '16px' : '0px' }}
        transition={{ type: 'spring', stiffness: 500, damping: 34 }}
        style={{
          height: '1px',
          background: SP.accentGrad,
          display: 'inline-block',
        }}
      />
      {name}
      {soon && (
        <span style={{
          fontFamily: MONO,
          fontSize: '8.5px',
          letterSpacing: '0.2em',
          color: SP.accentDeep,
          fontWeight: 700,
          padding: '2px 7px',
          borderRadius: '999px',
          background: SP.accentTint,
          border: `1px solid ${SP.accentTintB}`,
          marginLeft: '4px',
        }}>
          SOON
        </span>
      )}
    </span>
  );

  const commonStyle = {
    textDecoration: 'none',
    cursor: soon ? 'default' : 'pointer',
    opacity: soon ? 0.7 : 1,
  };

  if (external) {
    return (
      <li>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={commonStyle}
        >
          {content}
        </a>
      </li>
    );
  }

  return (
    <li>
      <a
        href={soon ? '#' : url}
        onClick={soon ? (e) => e.preventDefault() : undefined}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={commonStyle}
      >
        {content}
      </a>
    </li>
  );
}

/* ─── Bottom row: email (left) · copyright (center) · socials (right) ─ */
function BottomRow({ year, isMobile, isTablet }) {
  // On mobile we stack vertically; desktop/tablet is a 3-column flex.
  if (isMobile) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        textAlign: 'center',
      }}>
        <EmailLink />
        <Copyright year={year} />
        <SocialRow />
      </div>
    );
  }
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      alignItems: 'center',
      gap: '24px',
    }}>
      <div style={{ justifySelf: 'start' }}>
        <EmailLink />
      </div>
      <div style={{ justifySelf: 'center' }}>
        <Copyright year={year} />
      </div>
      <div style={{ justifySelf: 'end' }}>
        <SocialRow />
      </div>
    </div>
  );
}

/** Envelope + Nasalization email address. */
function EmailLink() {
  const [hover, setHover] = useState(false);
  return (
    <a
      href="mailto:support@daggpt.network"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '12px',
        textDecoration: 'none',
        color: hover ? SP.accentDeep : SP.inkSoft,
        transition: 'color 0.2s',
      }}
    >
      <EnvelopeIcon color={hover ? SP.accentA : SP.inkSoft} />
      <span style={{
        fontFamily: TECH,
        fontSize: '13px',
        letterSpacing: '0.08em',
        fontWeight: 500,
        textTransform: 'uppercase',
      }}>
        support@daggpt.network
      </span>
    </a>
  );
}

function Copyright({ year }) {
  return (
    <span style={{
      fontFamily: MONO,
      fontSize: '10.5px',
      letterSpacing: '0.22em',
      color: SP.subtle,
      fontWeight: 600,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}>
      © {year} DAGGPT · All rights reserved
    </span>
  );
}

function EnvelopeIcon({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}

function SocialRow() {
  const socials = [
    { name: 'GitHub', url: 'https://github.com/dagchain-official/daggpt-landing', glyph: <GithubIcon /> },
    { name: 'Email',  url: 'mailto:support@daggpt.network',                       glyph: <MailIcon   /> },
  ];
  return (
    <div style={{ display: 'inline-flex', gap: '8px' }}>
      {socials.map((s) => (
        <a
          key={s.name}
          href={s.url}
          aria-label={s.name}
          target={s.url.startsWith('http') ? '_blank' : undefined}
          rel={s.url.startsWith('http') ? 'noopener noreferrer' : undefined}
          style={{
            width: '38px',
            height: '38px',
            borderRadius: '12px',
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${SP.line}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: SP.inkSoft,
            textDecoration: 'none',
            transition: 'background 0.2s, border 0.2s, color 0.2s, transform 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = SP.accentTint;
            e.currentTarget.style.borderColor = SP.accentTintB;
            e.currentTarget.style.color = SP.accentDeep;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
            e.currentTarget.style.borderColor = SP.line;
            e.currentTarget.style.color = SP.inkSoft;
            e.currentTarget.style.transform = 'none';
          }}
        >
          {s.glyph}
        </a>
      ))}
    </div>
  );
}

/* ─── Bottom strip: copyright + version + location ────────────── */

/* ─── Minimal SVG icons (no Lucide clutter) ───────────────────── */
function TwitterIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
function GithubIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.1.79-.25.79-.55v-2.06c-3.2.7-3.88-1.37-3.88-1.37-.52-1.33-1.28-1.68-1.28-1.68-1.05-.72.08-.71.08-.71 1.16.08 1.77 1.19 1.77 1.19 1.03 1.77 2.71 1.26 3.37.96.1-.75.4-1.26.73-1.55-2.55-.29-5.24-1.27-5.24-5.66 0-1.25.45-2.28 1.18-3.08-.12-.29-.51-1.46.11-3.04 0 0 .96-.31 3.15 1.17.91-.25 1.89-.38 2.86-.39.97.01 1.95.14 2.86.39 2.19-1.48 3.15-1.17 3.15-1.17.62 1.58.23 2.75.11 3.04.74.8 1.18 1.83 1.18 3.08 0 4.4-2.7 5.37-5.26 5.65.41.35.78 1.05.78 2.12v3.14c0 .3.21.66.8.55C20.22 21.38 23.5 17.08 23.5 12 23.5 5.65 18.35.5 12 .5z" />
    </svg>
  );
}
function LinkedinIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.38-1.85 3.61 0 4.28 2.38 4.28 5.47zM5.34 7.43a2.07 2.07 0 110-4.14 2.07 2.07 0 010 4.14zM7.12 20.45H3.56V9h3.56zM22.22 0H1.78C.8 0 0 .78 0 1.74v20.52C0 23.22.8 24 1.78 24h20.44c.98 0 1.78-.78 1.78-1.74V1.74C24 .78 23.2 0 22.22 0z" />
    </svg>
  );
}
function MailIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  );
}

export default Footer;
